/* lc-map.v2.js — Lost Collective shared Mapbox utilities.
   Loaded only on pages that render a map (conditional in Liquid).
   All colours resolved from CSS custom properties at runtime — no
   hardcoded hex values.

   Renamed from lc-map.js on 2026-05-18 to bypass a stuck Shopify
   asset URL cache. Three sequential `theme push`es (PR #418/419/420)
   landed lcBuildPinImage radius changes (7→10→20) in the Admin asset
   store, but Shopify never bumped the embedded `t/N/?v=` URL hash,
   so Cloudflare kept serving the pre-PR-#418 bundle with a 1-year
   `max-age`. A new asset path guarantees a fresh URL with no cache
   entry. */

/**
 * Resolve a CSS custom property from :root to its computed string value.
 * @param {string} prop - e.g. '--lc-paper'
 * @returns {string}
 */
function lcToken(prop) {
  return getComputedStyle(document.documentElement).getPropertyValue(prop).trim();
}

function lcEscape(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert a hex colour (#rrggbb) to an `rgba(r, g, b, a)` string.
 * Used so the ochre glow ring under the pin can carry an opacity
 * the source token doesn't define. Falls back gracefully when the
 * token isn't a hex literal.
 * @param {string} hex
 * @param {number} alpha 0..1
 * @returns {string}
 */
function lcHexToRgba(hex, alpha) {
  const v = String(hex || '').trim();
  if (!v.startsWith('#')) return v;
  const h = v.replace('#', '');
  const f = h.length === 3
    ? h.split('').map((c) => c + c).join('')
    : h.padEnd(6, '0').slice(0, 6);
  const r = parseInt(f.slice(0, 2), 16);
  const g = parseInt(f.slice(2, 4), 16);
  const b = parseInt(f.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Apply Lost Collective brand styling over Mapbox light-v11.
 * Call inside map.on('load', ...).
 * @param {mapboxgl.Map} map
 */
function applyLCMapStyle(map) {
  const paper     = lcToken('--lc-paper');
  const paperDeep = lcToken('--lc-paper-deep');
  const stone     = lcToken('--lc-stone');
  const ash       = lcToken('--lc-ash');
  const ink       = lcToken('--lc-ink');

  // Two distinct failure modes need guarding:
  //   1. Layer missing from active style — fires an error EVENT (not a
  //      throw), so try/catch can't suppress; needs map.getLayer() check.
  //      PR #418's original guard, surfaced by the 5 unresearched-sample
  //      collections in PR #416's P1 Mapbox cluster.
  //   2. Layer exists but the paint property isn't valid for that layer
  //      type (e.g. icon-opacity on a layer without an icon) — Mapbox
  //      throws synchronously here, so wrap in try/catch.
  //      Surfaced 2026-05-18 when the lc-map.v2.js rename finally
  //      delivered the post-PR-#418 code to live and applyLCMapStyle
  //      began halting the section's load handler before addLCPinLayers
  //      could run, breaking pin rendering entirely.
  const safeSet = (id, prop, value) => {
    if (!map.getLayer(id)) return;
    try { map.setPaintProperty(id, prop, value); } catch (_e) { /* prop not valid for this layer type */ }
  };

  // Background / land
  safeSet('background', 'background-color', paper);
  ['land', 'landcover', 'landuse', 'landuse-shadow', 'land-structure'].forEach(id => {
    safeSet(id, 'fill-color', paperDeep);
  });

  // Water — muted stone surface, not blue
  ['water', 'water-shadow'].forEach(id => {
    safeSet(id, 'fill-color', stone);
    safeSet(id, 'line-color', stone);
  });

  // Roads — major at 0.25 opacity, minor hidden
  const majorRoadIds = [
    'road-motorway-trunk', 'road-primary', 'road-secondary-tertiary',
    'road-motorway-trunk-case', 'road-primary-case', 'road-secondary-tertiary-case',
  ];
  majorRoadIds.forEach(id => {
    safeSet(id, 'line-color', ash);
    safeSet(id, 'line-opacity', 0.25);
  });

  const hiddenRoadIds = [
    'road-street', 'road-street-low', 'road-path', 'road-path-bg',
    'road-service-link', 'road-link', 'road-pedestrian', 'road-pedestrian-case',
    'road-simple', 'ferry', 'ferry-auto',
  ];
  hiddenRoadIds.forEach(id => {
    safeSet(id, 'line-opacity', 0);
  });

  // Admin boundaries — hide state/province lines (they create a white grid at low zoom)
  ['admin-1-boundary', 'admin-1-boundary-bg', 'admin-0-boundary-disputed'].forEach(id => {
    safeSet(id, 'line-opacity', 0);
  });
  // Keep country outline, very faint
  safeSet('admin-0-boundary', 'line-color', ash);
  safeSet('admin-0-boundary', 'line-opacity', 0.2);

  // Labels — country/state visible (ash), city visible (ink), street/POI hidden
  ['country-label', 'state-label'].forEach(id => {
    safeSet(id, 'text-color', ash);
    safeSet(id, 'text-halo-color', paper);
  });

  ['settlement-label', 'settlement-subdivision-label'].forEach(id => {
    safeSet(id, 'text-color', ink);
    safeSet(id, 'text-halo-color', paper);
  });

  ['road-label', 'road-number-shield', 'poi-label', 'transit-label',
   'airport-label', 'natural-point-label', 'natural-line-label',
   'waterway-label', 'building-number-label'].forEach(id => {
    safeSet(id, 'text-opacity', 0);
    safeSet(id, 'icon-opacity', 0);
  });
}

/**
 * Draw a teardrop location-pin on a canvas and return ImageData for
 * map.addImage(). The pin head is a circle; the body tapers to a point
 * at the bottom. Fill: ochre. Stroke: ink. Optional inner highlight dot.
 *
 * @param {string} ochre  — hex fill colour
 * @param {string} ink    — hex stroke colour
 * @param {number} r      — circle-head radius in px
 * @returns {{ width: number, height: number, data: Uint8ClampedArray }}
 */
function lcBuildPinImage(ochre, ink, r) {
  // Draw at 4× for crisp rendering on retina + high-DPR mobile — caller
  // passes pixelRatio:4. The Mapbox icon downsamples cleanly at display
  // time; without this oversampling the bezier-stroked teardrop edges
  // visibly aliased on iOS Safari at DPR=3.
  const s        = 4;
  const sr       = r * s;
  const cx       = sr + 3;
  const w        = cx * 2;
  const circleCY = sr + 3;
  const tipY     = circleCY + sr * 2.1;
  const h        = Math.ceil(tipY) + 4;

  const canvas   = document.createElement('canvas');
  canvas.width   = w;
  canvas.height  = h;
  const ctx      = canvas.getContext('2d');

  // Straight-sided body, tangent to the circle. The sides leave the
  // circle at its tangent points (where a line from the tip touches
  // the circle), so the transition from arc to line is smooth (no
  // kink) and the bottom converges to a sharp point. Geometry:
  //   tangent point P satisfies CP perpendicular to PT, |CP|=sr.
  //   Solving: P_y - circleCY = sr² / d, P_x - cx = ±sr·√(d²-sr²)/d.
  const d        = tipY - circleCY;
  const tangentX = sr * Math.sqrt(d * d - sr * sr) / d;
  const tangentY = sr * sr / d;
  const leftAng  = Math.atan2(tangentY, -tangentX);
  const rightAng = Math.atan2(tangentY,  tangentX);

  ctx.beginPath();
  ctx.moveTo(cx + tangentX, circleCY + tangentY);  // right tangent point
  ctx.lineTo(cx, tipY);                             // straight down to tip
  ctx.lineTo(cx - tangentX, circleCY + tangentY);   // straight up to left tangent
  // Upper arc from left tangent over the top of the circle to right tangent.
  ctx.arc(cx, circleCY, sr, leftAng, rightAng, false);
  ctx.closePath();

  ctx.fillStyle   = ochre;
  ctx.fill();
  ctx.strokeStyle = ink;
  ctx.lineWidth   = 1.5 * s;
  ctx.stroke();

  // Inner dot — large enough to read, white at 70% opacity.
  ctx.beginPath();
  ctx.arc(cx, circleCY, sr * 0.44, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.70)';
  ctx.fill();

  const id = ctx.getImageData(0, 0, w, h);
  return { width: w, height: h, data: id.data };
}

/**
 * Add LC teardrop pin markers. Two icon images (normal + hover) are
 * registered with the map, then a single symbol layer binds them to the
 * source. Source ID is the caller's responsibility.
 *
 * @param {mapboxgl.Map} map
 * @param {string} sourceId
 * @param {object} [opts]
 * @param {string} [opts.idPrefix='lc-pin']
 * @returns {{ glowLayerId: string, pinLayerId: string }}
 */
function addLCPinLayers(map, sourceId, opts) {
  const idPrefix = (opts && opts.idPrefix) || 'lc-pin';
  const ochre    = lcToken('--lc-ochre');
  const ink      = lcToken('--lc-ink');

  const imgId      = idPrefix + '-img';
  const pinLayerId = idPrefix + '-pin';

  if (!map.hasImage(imgId)) {
    map.addImage(imgId, lcBuildPinImage(ochre, ink, 13), { pixelRatio: 4 });
  }

  map.addLayer({
    id: pinLayerId,
    type: 'symbol',
    source: sourceId,
    layout: {
      'icon-image': imgId,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 1,
    },
  });

  return { glowLayerId: pinLayerId, pinLayerId: pinLayerId };
}

/**
 * Build popup HTML for a single pin.
 * @param {{ title: string, url: string, series_title?: string }} pin
 * @returns {string}
 */
function buildLCPopup(pin) {
  const safeTitle  = lcEscape(pin.title);
  const safeUrl    = (pin.url && !pin.url.startsWith('javascript:')) ? pin.url : '/';
  const seriesLine = pin.series_title
    ? `<div class="lc-map-popup__series">${lcEscape(pin.series_title)}</div>`
    : '';
  return `
    <div class="lc-map-popup">
      <div class="lc-map-popup__title">${safeTitle}</div>
      ${seriesLine}
      <a class="lc-map-popup__link" href="${lcEscape(safeUrl)}">View print &rarr;</a>
    </div>
  `;
}

/**
 * Detect whether this session can create a WebGL context.
 *
 * Mapbox GL JS v3 dropped the old `mapboxgl.supported()` pre-flight, so
 * without a guard `new mapboxgl.Map()` throws an uncaught
 * "Failed to initialize WebGL." on any browser/device that can't get a
 * WebGL context — older Safari, hardware acceleration off, GPU on a
 * blocklist, or an exhausted context pool. That throw was Sentry
 * LC-DAWN-STOREFRONT-C (LOS7-346), firing across every series collection
 * page for ~16 visitors.
 *
 * Broad probe (webgl2 OR webgl): catches the common "no WebGL at all"
 * case. The webgl2-required-but-only-webgl1 case is left to createMap's
 * try/catch so a single helper covers every failure mode.
 *
 * @returns {boolean}
 */
function lcWebglSupported() {
  try {
    var canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl2') ||
        canvas.getContext('webgl') ||
        canvas.getContext('experimental-webgl'))
    );
  } catch (_e) {
    return false;
  }
}

/**
 * Construct a Mapbox map behind a WebGL pre-flight and a try/catch so a
 * WebGL-incapable session degrades to no map instead of an uncaught
 * throw. On failure it hides each element in `failEls` (so no empty map
 * frame is left behind) and returns null — callers should `return` when
 * the result is falsy.
 *
 * SCOPE — this guard covers CONSTRUCTION ONLY. `new mapboxgl.Map()` returns
 * as soon as the map is built; everything after that runs in the async
 * render loop, on requestAnimationFrame frames this try/catch has already
 * returned from. So it cannot catch a throw from _render / _calcMatrices /
 * coveringTiles, which is the whole LC-DAWN-STOREFRONT-1E/1F/1G/1H class
 * (LOS7-2981 — `mat4.invert` returning null leaves transform.invProjMatrix
 * poisoned). Do not assume a map that got past here is guarded for life.
 *
 * @param {object} options - mapboxgl.Map options (must include container)
 * @param {Array<string|Element>} [failEls] - wrappers to hide on failure
 * @returns {mapboxgl.Map|null}
 */
function lcCreateMap(options, failEls) {
  var hideFallback = function () {
    (failEls || []).forEach(function (el) {
      var node = typeof el === 'string' ? document.querySelector(el) : el;
      if (node) node.style.display = 'none';
    });
  };

  if (!window.mapboxgl || !lcWebglSupported()) {
    hideFallback();
    return null;
  }

  try {
    return new window.mapboxgl.Map(options);
  } catch (_e) {
    hideFallback();
    return null;
  }
}

window.lcMap = {
  applyLCMapStyle,
  addLCPinLayers,
  buildLCPopup,
  lcToken,
  lcEscape,
  lcHexToRgba,
  webglSupported: lcWebglSupported,
  createMap: lcCreateMap,
};
